(() => {
  if (window.AGLazyImages) return;

  const selector = 'img.lazyload';
  const observed = new WeakSet();
  const widths = new WeakMap();
  const pictureSources = image => image.parentElement?.tagName === 'PICTURE'
    ? image.parentElement.querySelectorAll('source') : [];
  const fire = (image, name, cancelable = false) => image.dispatchEvent(
    new CustomEvent(name, { bubbles: true, cancelable, detail: {} })
  );

  // ResizeObserver supplies dimensions without scanning or measuring the page.
  const setSize = (image, width) => {
    width = Math.ceil(width);
    if (width <= 0 || widths.get(image) === width) return;
    widths.set(image, width);
    image.sizes = `${width}px`;
    pictureSources(image).forEach(source => {
      if (source.dataset.sizes === 'auto') source.sizes = image.sizes;
    });
  };
  const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(entries => {
    entries.forEach(entry => setSize(entry.target, entry.contentRect.width));
  }) : null;

  const unveil = (image, width) => {
    if (!image.matches(selector) || !fire(image, 'lazybeforeunveil', true)) return;
    const sizes = image.dataset.sizes;
    if (sizes === 'auto') {
      setSize(image, width || image.getBoundingClientRect().width);
      if (!widths.has(image)) image.sizes = '100vw';
      resize?.observe(image);
    } else if (sizes) {
      image.sizes = sizes;
    }
    const complete = () => {
      image.removeEventListener('load', loaded);
      image.removeEventListener('error', failed);
      image.classList.remove('lazyloading');
    };
    const loaded = () => {
      complete();
      image.classList.add('lazyloaded');
      fire(image, 'lazyloaded');
    };
    const failed = () => {
      complete();
      image.classList.add('lazyerror');
    };
    image.addEventListener('load', loaded);
    image.addEventListener('error', failed);
    image.classList.replace('lazyload', 'lazyloading');
    image.loading = 'eager';
    pictureSources(image).forEach(source => {
      if (!source.dataset.srcset) return;
      if (sizes !== 'auto' && source.dataset.sizes) source.sizes = source.dataset.sizes;
      source.srcset = source.dataset.srcset;
    });
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    if (image.dataset.src) image.src = image.dataset.src;
    intersection?.unobserve(image);
    // Cached images can finish before the next task, without another load event.
    if (image.complete && image.naturalWidth > 0) loaded();
  };
  const intersection = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) unveil(entry.target, entry.boundingClientRect.width);
    });
  }, { rootMargin: '300px 0px' }) : null;

  const observe = image => {
    if (observed.has(image)) return;
    observed.add(image);
    if (intersection) intersection.observe(image);
    else unveil(image);
  };
  const init = root => {
    if (root.matches?.(selector)) observe(root);
    root.querySelectorAll?.(selector).forEach(observe);
  };
  const mutations = new MutationObserver(records => {
    records.forEach(record => {
      record.addedNodes.forEach(init);
      record.removedNodes.forEach(node => {
        const release = image => {
          if (image.isConnected) return;
          intersection?.unobserve(image);
          resize?.unobserve(image);
          observed.delete(image);
        };
        if (node.matches?.('img')) release(node);
        node.querySelectorAll?.('img').forEach(release);
      });
    });
  });
  mutations.observe(document.documentElement, { childList: true, subtree: true });
  window.AGLazyImages = { init, unveil };
  init(document);
})();
